/**
 * Entity groups — lane A9's definition, fixed for the whole lane.
 *
 * No table carries an "entity group" today (innovateflow-aggregator-entities has
 * no group field), so the group is derived on the client from the two payloads
 * the aggregator screens already fetch: AggregatorEntity[] and AggregatorProject[].
 *
 * Group key = the company label the aggregator holds for an inventor, i.e.
 * AggregatorEntity.entity_name / AggregatorProject.entity_name (grouped exactly
 * as written — no trimming, no case folding, no derived "company" string).
 *
 * This module is pure: no React, no fetching, no imports from services/api.ts.
 */
import type { AggregatorEntity, AggregatorProject } from '../../types'

export interface EntityGroup {
  key: string
  label: string
  members: AggregatorEntity[]
  projects: AggregatorProject[]
  otherOwnerCount: number
}

/**
 * Group entities and projects by entity_name, or by the portfolio's own
 * override for that inventor where it has set one (figure C1's editable Entity
 * group field — see entityGroupNames.ts). Includes groups that have projects
 * but no entity row, and groups that have an entity row but no projects.
 * Sorted by projects.length descending, then label ascending, so the order is
 * deterministic and a snapshot cannot flap.
 *
 * The override is applied HERE, in the one function every screen groups
 * through, so S5 and S6 can never put the same inventor in different groups.
 */
export function buildEntityGroups(
  entities: AggregatorEntity[],
  projects: AggregatorProject[],
  /** entity_id -> the group name this portfolio has set. Empty by default. */
  groupNames: Record<string, string> = {},
): EntityGroup[] {
  const labelOf = (entityId: string | undefined, recorded: string | undefined) => {
    const override = entityId ? groupNames[entityId]?.trim() : ''
    return override || recorded
  }
  // Real dev data carries legacy grant rows with no resource_type and no
  // entity_name stamped (measured 4 Sep 2026: two of roger's aggregator's 40
  // grants predate the entity_name stamping path) — a project or entity
  // missing entity_name cannot be grouped, so it is excluded rather than
  // grouped under an undefined label, which crashed the sort's
  // localeCompare on the live dev estate.
  const labels = new Set<string>()
  for (const e of entities) {
    const label = labelOf(e.entity_id, e.entity_name)
    if (label) labels.add(label)
  }
  for (const p of projects) {
    const label = labelOf(p.entity_id, p.entity_name)
    if (label) labels.add(label)
  }

  const groups: EntityGroup[] = Array.from(labels).map((label) => {
    const members = entities.filter((e) => labelOf(e.entity_id, e.entity_name) === label)
    const groupProjects = projects.filter((p) => labelOf(p.entity_id, p.entity_name) === label)
    const memberIds = new Set(members.map((m) => m.entity_id))
    const distinctNonMemberOwners = new Set(
      groupProjects
        .map((p) => p.entity_id)
        .filter((id) => !memberIds.has(id)),
    )
    return {
      key: label,
      label,
      members,
      projects: groupProjects,
      otherOwnerCount: distinctNonMemberOwners.size,
    }
  })

  return groups.sort((a, b) => {
    if (b.projects.length !== a.projects.length) return b.projects.length - a.projects.length
    return a.label.localeCompare(b.label)
  })
}

/** Find a group by its decoded label. */
export function findEntityGroup(groups: EntityGroup[], groupName: string): EntityGroup | undefined {
  return groups.find((g) => g.label === groupName)
}

/** Every link in this lane goes through this, so encoding is decided in one place. */
export function entityGroupPath(label: string): string {
  return `/agg/entity/group/${encodeURIComponent(label)}`
}

/**
 * The design's required substring is "other owner" in all three forms —
 * including the honest zero case, which states the empty state plainly
 * instead of hiding the control.
 */
export function otherOwnerLabel(count: number): string {
  if (count === 0) return 'No other owner holds a row here.'
  if (count === 1) return 'and 1 other owner'
  return `and ${count} other owners`
}

/**
 * Story #5648 bug #5840 — Figure 7's "Grants expire" row, computed rather than
 * asserted.
 *
 * The row used to be a constant "No grants expiring" because nothing ever
 * looked. It now reads the per-project `grant_expires_at` that
 * aggregator/handler.get_portfolio_projects stamps from the grant's own
 * permission row, and distinguishes three genuinely different states:
 *
 *   tracked=false  the payload carries no expiry field at all -> "not tracked".
 *                  We have not checked, so we must not claim there is none.
 *   count === 0    every grant was read and none of them expires.
 *   count > 0      how many expire, and the earliest date one does.
 *
 * Grants that have ALREADY expired are not counted: the aggregator read path
 * (_is_grant_valid) drops them before the project ever reaches this screen, so
 * a past date here would be a row for a grant the screen is not showing.
 */
export interface GrantExpirySummary {
  tracked: boolean
  count: number
  earliest?: string
}

export function summariseGrantExpiry(
  projects: Array<{ grant_expires_at?: string | number | null }>,
  now: Date = new Date(),
): GrantExpirySummary {
  const tracked = projects.some((p) => 'grant_expires_at' in p)
  if (!tracked) return { tracked: false, count: 0 }

  const dates: Date[] = []
  for (const project of projects) {
    const raw = project.grant_expires_at
    if (raw === undefined || raw === null || raw === '') continue
    // The permission row stores an epoch (seconds) or an ISO-8601 string —
    // aggregator_grants/handler._normalize_expires_at accepts both, so both
    // can arrive here.
    const numeric = typeof raw === 'number' ? raw : Number(raw)
    const parsed = Number.isFinite(numeric) ? new Date(numeric * 1000) : new Date(String(raw))
    if (Number.isNaN(parsed.getTime())) continue
    if (parsed.getTime() <= now.getTime()) continue
    dates.push(parsed)
  }
  if (dates.length === 0) return { tracked: true, count: 0 }
  dates.sort((a, b) => a.getTime() - b.getTime())
  return { tracked: true, count: dates.length, earliest: dates[0].toISOString() }
}

/**
 * Stage cell for Figure 8's Portfolio-entities table (Bug #5852).
 *
 * The platform records no funding stage on an entity or a project, and the
 * round-1 build printed a hardcoded "Active"/"Unrostered" that never read a
 * row. This reads the group's REAL project statuses: one status → that word;
 * several → each with its count; none → "No projects". It never invents a
 * stage the data does not carry.
 */
export function summariseStage(projects: { status?: string }[]): string {
  if (projects.length === 0) return 'No projects'
  const counts = new Map<string, number>()
  for (const p of projects) {
    const key = (p.status || 'unknown').toLowerCase()
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  const label = (k: string) => k.charAt(0).toUpperCase() + k.slice(1).replace(/_/g, ' ')
  if (counts.size === 1) return label([...counts.keys()][0])
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([k, n]) => `${label(k)} ${n}`)
    .join(' · ')
}

/**
 * Figure 8's relative "Last activity" ("3d ago", "yesterday", "1w ago").
 * `now` is injectable so tests are deterministic. Returns null for an
 * unparseable value so the caller can print its own honest fallback.
 */
export function formatLastActivity(iso: string | undefined, now: number = Date.now()): string | null {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return null
  const days = Math.floor((now - t) / 86_400_000)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 7) return `${days}d ago`
  if (days < 30) return `${Math.floor(days / 7)}w ago`
  if (days < 365) return `${Math.floor(days / 30)}mo ago`
  return `${Math.floor(days / 365)}y ago`
}
